/**
 * PasswordStrengthChecker — lightweight, industry-grade strength meter.
 *
 * Scoring approach (0–4 scale matching zxcvbn convention):
 *  0 = Very weak   — trivial to guess
 *  1 = Weak        — basic brute force
 *  2 = Fair        — moderate resistance
 *  3 = Strong      — resistant to targeted attacks
 *  4 = Very strong — resistant to offline cracking
 *
 * Checks: length, character class diversity, entropy estimate,
 * common passwords, sequential/repeated patterns, keyboard patterns.
 */
import React, { useMemo } from 'react';

// ---------- Common password blacklist (top 200 compressed) ----------
const COMMON_PASSWORDS = new Set([
  'password','123456','12345678','qwerty','abc123','monkey','1234567','letmein',
  'trustno1','dragon','baseball','iloveyou','master','sunshine','ashley','bailey',
  'shadow','123123','654321','superman','qazwsx','michael','football','password1',
  'password123','batman','login','welcome','welcome1','admin','admin123','root',
  'toor','passw0rd','p@ssw0rd','p@ssword','master123','changeme','princess',
  'qwerty123','letmein1','abc1234','111111','1234','12345','123456789','1234567890',
  'welcome123','iloveyou1','sunshine1','!@#$%^&*','password!','trustno1!',
]);

// Keyboard row sequences (US QWERTY)
const KEYBOARD_SEQS = [
  'qwertyuiop', 'asdfghjkl', 'zxcvbnm',
  '1234567890', '!@#$%^&*()',
  'qazwsx', 'wsxedc', 'edcrfv', 'rfvtgb', 'tgbyhn', 'yhnujm',
];

// ---------- Scoring engine ----------

function getCharClasses(pw) {
  return {
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^a-zA-Z0-9]/.test(pw),
  };
}

function estimateEntropy(pw) {
  const classes = getCharClasses(pw);
  let poolSize = 0;
  if (classes.lower) poolSize += 26;
  if (classes.upper) poolSize += 26;
  if (classes.digit) poolSize += 10;
  if (classes.special) poolSize += 33;
  if (poolSize === 0) poolSize = 26;
  return pw.length * Math.log2(poolSize);
}

function hasSequentialChars(pw, minRun = 3) {
  const lower = pw.toLowerCase();
  for (let i = 0; i <= lower.length - minRun; i++) {
    let ascending = true, descending = true;
    for (let j = 1; j < minRun; j++) {
      if (lower.charCodeAt(i + j) !== lower.charCodeAt(i + j - 1) + 1) ascending = false;
      if (lower.charCodeAt(i + j) !== lower.charCodeAt(i + j - 1) - 1) descending = false;
    }
    if (ascending || descending) return true;
  }
  return false;
}

function hasRepeatedChars(pw, minRun = 3) {
  return new RegExp(`(.)\\1{${minRun - 1},}`).test(pw);
}

function hasKeyboardPattern(pw, minLen = 4) {
  const lower = pw.toLowerCase();
  for (const seq of KEYBOARD_SEQS) {
    for (let i = 0; i <= seq.length - minLen; i++) {
      const fwd = seq.slice(i, i + minLen);
      const rev = fwd.split('').reverse().join('');
      if (lower.includes(fwd) || lower.includes(rev)) return true;
    }
  }
  return false;
}

function isCommon(pw) {
  const lower = pw.toLowerCase();
  if (COMMON_PASSWORDS.has(lower)) return true;
  // Also check with common leet substitutions reversed
  const deleet = lower
    .replace(/@/g, 'a').replace(/0/g, 'o').replace(/1/g, 'l')
    .replace(/3/g, 'e').replace(/\$/g, 's').replace(/5/g, 's')
    .replace(/!/g, 'i').replace(/7/g, 't');
  return COMMON_PASSWORDS.has(deleet);
}

/**
 * Returns { score: 0-4, label, feedback[] }
 */
export function scorePassword(pw) {
  if (!pw) return { score: 0, label: '', feedback: [] };

  const feedback = [];
  let score = 0;

  // Common password check — instant fail
  if (isCommon(pw)) {
    return { score: 0, label: 'Very weak', feedback: ['This is a commonly used password'] };
  }

  // Length scoring
  if (pw.length >= 16) score += 2;
  else if (pw.length >= 12) score += 1.5;
  else if (pw.length >= 10) score += 1;
  else if (pw.length >= 8) score += 0.5;
  else feedback.push('Use at least 10 characters');

  // Character class diversity
  const classes = getCharClasses(pw);
  const classCount = Object.values(classes).filter(Boolean).length;
  if (classCount === 4) score += 1.5;
  else if (classCount === 3) score += 1;
  else if (classCount === 2) score += 0.5;

  if (!classes.upper) feedback.push('Add uppercase letters');
  if (!classes.lower) feedback.push('Add lowercase letters');
  if (!classes.digit) feedback.push('Add numbers');
  if (!classes.special) feedback.push('Add special characters (!@#$%…)');

  // Entropy bonus
  const entropy = estimateEntropy(pw);
  if (entropy >= 60) score += 1;
  else if (entropy >= 45) score += 0.5;

  // Penalties
  if (hasSequentialChars(pw, 4)) { score -= 0.5; feedback.push('Avoid sequential characters (abcd, 1234)'); }
  if (hasRepeatedChars(pw, 3)) { score -= 0.5; feedback.push('Avoid repeated characters (aaa, 111)'); }
  if (hasKeyboardPattern(pw, 4)) { score -= 0.5; feedback.push('Avoid keyboard patterns (qwerty, asdf)'); }

  // Clamp to 0-4
  const finalScore = Math.max(0, Math.min(4, Math.round(score)));
  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

  if (finalScore >= 3 && feedback.length === 0) {
    // All good
  } else if (finalScore < 2 && feedback.length === 0) {
    feedback.push('Try making it longer and more varied');
  }

  return { score: finalScore, label: labels[finalScore], feedback };
}

/**
 * Returns true if the password meets the minimum policy for submission.
 * Policy: score >= 3, length >= 10, all 4 char classes present, no repeats.
 */
export function meetsPolicy(pw) {
  if (!pw || pw.length < 10) return false;
  const classes = getCharClasses(pw);
  if (!classes.lower || !classes.upper || !classes.digit || !classes.special) return false;
  if (hasRepeatedChars(pw, 3)) return false;
  if (isCommon(pw)) return false;
  return true;
}


// ---------- Visual component ----------

const COLORS = ['#dc2626', '#ef4444', '#f59e0b', '#22c55e', '#059669'];
const BG_COLORS = ['#fef2f2', '#fef2f2', '#fffbeb', '#f0fdf4', '#ecfdf5'];

export default function PasswordStrengthChecker({ password }) {
  const { score, label, feedback } = useMemo(() => scorePassword(password || ''), [password]);

  if (!password) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Strength bar */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= score && password.length > 0 ? COLORS[score] : '#e5e7eb',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* Label */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: feedback.length > 0 ? 6 : 0,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS[score] }}>
          {label}
        </span>
        {meetsPolicy(password) && (
          <span style={{ fontSize: 11, color: '#059669', fontWeight: 500 }}>
            ✓ Meets policy
          </span>
        )}
      </div>

      {/* Feedback */}
      {feedback.length > 0 && (
        <div style={{
          padding: '8px 10px', borderRadius: 6,
          background: BG_COLORS[score], fontSize: 12,
          color: '#374151',
        }}>
          {feedback.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i < feedback.length - 1 ? 3 : 0 }}>
              <span style={{ color: COLORS[score], fontSize: 10 }}>●</span>
              {f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
